import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

// Import the filter primitives DIRECTLY (not via the barrel) so the heavy
// `<DateRangeFilter>` → `@mui/x-date-pickers` dep the barrel re-exports
// isn't pulled into the routed bundle — the inspectors page has no date
// filter.
import { FilterBar } from '../../../components/filters/FilterBar';
import { SearchInput } from '../../../components/filters/SearchInput';
import { StatusMultiSelect } from '../../../components/filters/StatusMultiSelect';
import { PlaceSelect } from '../../../components/place';
import { MIN_SEARCH_LENGTH, STATUS_OPTIONS, type InspectorFilters } from '../_lib/inspectorFilters';

/**
 * Filter toolbar for the inspectors list (AUT-125) — search + status
 * multi-select + city free-text. Extracted from the page so the page
 * stays under the line cap. Every value is URL-state-driven via the
 * parent's `setFilters` patch.
 *
 * City is a small free-text field (server applies ILIKE — opaque to the
 * SPA). The mockup shows a chip + dropdown, but with city free-text on
 * the backend the simplest correct UI is a labelled TextField.
 */
export interface InspectorsFilterBarProps {
  filters: InspectorFilters;
  setFilters: (
    patch: Partial<Record<keyof InspectorFilters, string | number | string[] | null>>,
  ) => void;
  canClear: boolean;
  onClear: () => void;
}

export const InspectorsFilterBar = ({
  filters,
  setFilters,
  canClear,
  onClear,
}: InspectorsFilterBarProps): ReactElement => {
  const { t } = useTranslation();
  return (
    <FilterBar canClear={canClear} onClear={onClear}>
      <SearchInput
        initialValue={filters.q ?? ''}
        placeholder={t('inspectors.search.placeholder')}
        minLength={MIN_SEARCH_LENGTH}
        onSearch={(q) => setFilters({ q: q || null })}
      />
      <StatusMultiSelect
        status={filters.status ?? []}
        options={STATUS_OPTIONS}
        label={t('inspectors.filters.statusLabel')}
        onChange={(next) => setFilters({ status: next })}
      />
      <PlaceSelect
        mode="filter"
        testId="inspectors-filter-place"
        value={{
          // A legacy `?city=Минск` has no country: Belarus.
          cityCountry: filters.cityCountry ?? (filters.city ? 'BY' : ''),
          city: filters.city ?? '',
          cityCustom: false,
        }}
        onChange={(p) => setFilters({ cityCountry: p.cityCountry || null, city: p.city || null })}
      />
    </FilterBar>
  );
};
